import { brassDict } from '../../utils/Dictionaries/brassDict';
import { scalesDict } from '../../utils/Dictionaries/scalesDict';

export const MIN_BPM = 30;
export const MAX_BPM = 300;
export const MAX_COUNT_IN = 8;

const ID_PATTERN = /^([A-G][b#]?)(\d)$/;

const PITCH_CLASS = {
  C: 0,
  'B#': 0,
  'C#': 1,
  Db: 1,
  D: 2,
  'D#': 3,
  Eb: 3,
  E: 4,
  Fb: 4,
  'E#': 5,
  F: 5,
  'F#': 6,
  Gb: 6,
  G: 7,
  'G#': 8,
  Ab: 8,
  A: 9,
  'A#': 10,
  Bb: 10,
  B: 11,
  Cb: 11,
};

const CONCERT_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

// Written pitch -> concert pitch, in semitones. A Bb trumpet sounds a major
// 2nd below what it reads; an F horn sounds a perfect 5th below. Trombone,
// euphonium and tuba are read at concert pitch, so they are not listed.
const TO_CONCERT = { trumpet: -2, frenchHorn: -7 };

export const parseScaleId = (id) => {
  const match = ID_PATTERN.exec(id);
  return match ? { tonic: match[1], octave: Number(match[2]) } : null;
};

/** The concert-pitch key for a written key, or null for non-transposing instruments. */
export const concertTonic = (instrument, tonic) => {
  const shift = TO_CONCERT[instrument];
  const pitchClass = PITCH_CLASS[tonic];
  if (!shift || pitchClass === undefined) return null;
  return CONCERT_NAMES[(((pitchClass + shift) % 12) + 12) % 12];
};

const isPlayable = (instrument, notes) => {
  const fingerings = brassDict[instrument];
  if (!fingerings) return true;
  return notes.every((note) => fingerings[note]);
};

const getScaleIds = (instrument) =>
  Object.keys(scalesDict[instrument] || {}).filter(
    (id) => parseScaleId(id) && scalesDict[instrument][id]?.length > 0,
  );

/** Every tonic this instrument has a scale for, in chromatic order from C. */
export const getKeyChoices = (instrument) => {
  const tonics = [...new Set(getScaleIds(instrument).map((id) => parseScaleId(id).tonic))];
  return tonics.sort(
    (a, b) => PITCH_CLASS[a] - PITCH_CLASS[b] || a.localeCompare(b),
  );
};

/**
 * Octaves available for a tonic. Only octaves whose notes all have fingerings
 * are offered, so the player can never land on a note the game can't check.
 */
export const getOctaveChoices = (instrument, tonic) => {
  const all = getScaleIds(instrument)
    .map(parseScaleId)
    .filter((parsed) => parsed.tonic === tonic)
    .map((parsed) => parsed.octave)
    .sort((a, b) => a - b);

  const playable = all.filter((octave) =>
    isPlayable(instrument, scalesDict[instrument][`${tonic}${octave}`]),
  );
  return playable.length > 0 ? playable : all;
};

/**
 * Turns a saved tonic + octave into a scale id that exists for this
 * instrument. Switching instruments or keys can leave the old octave
 * unavailable, so this falls back to the nearest octave, then to any scale.
 */
export const resolveScaleId = (instrument, tonic, octave) => {
  const octaves = getOctaveChoices(instrument, tonic);
  if (octaves.length > 0) {
    const nearest = octaves.reduce((best, candidate) =>
      Math.abs(candidate - octave) < Math.abs(best - octave) ? candidate : best,
    );
    return `${tonic}${nearest}`;
  }
  return getScaleIds(instrument)[0] || null;
};

/** First fingering for a note as a list of key names (valves, or the slide position). */
export const getCorrectKeys = (instrument, note) => {
  const combos = brassDict[instrument]?.[note] || [];
  const first = combos[0];
  if (typeof first === 'string') return first.split('');
  if (Array.isArray(first)) return first.map((key) => String(key));
  return [];
};

// Same keys the original stats object had (E1 up to C6), generated instead of
// typed out. The results graph endpoint receives this exact shape.
const STAT_NAMES = [
  'Cb', 'C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'E#', 'Fb', 'F',
  'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B', 'B#',
];

export const buildEmptyStats = () => {
  const stats = {};
  STAT_NAMES.slice(STAT_NAMES.indexOf('E')).forEach((name) => {
    stats[`${name}1`] = [0, 0];
  });
  for (let octave = 2; octave <= 5; octave += 1) {
    STAT_NAMES.forEach((name) => {
      stats[`${name}${octave}`] = [0, 0];
    });
  }
  stats.Cb6 = [0, 0];
  stats.C6 = [0, 0];
  return stats;
};

const normalise = (combo) =>
  [...combo]
    .map((key) => String(key))
    .sort()
    .join(',');

/**
 * True when the keys held right now are one of the note's accepted fingerings.
 * `combos` is the brassDict entry (a list of fingerings, each a string like
 * "jk" or an array of keys). Holding nothing is the open fingering, and on
 * trombone it is first position.
 */
export const matchesFingering = (instrument, heldKeys, combos) => {
  const list = Array.isArray(combos) ? combos : combos ? [combos] : [];
  const held = instrument === 'trombone' && heldKeys.length === 0 ? ['1'] : heldKeys;
  const heldId = normalise(held);
  return list.some((combo) => {
    if (typeof combo === 'string' || Array.isArray(combo)) return normalise(combo) === heldId;
    return false;
  });
};

// French horn is a left-hand instrument: the index finger ('e') is valve 1.
const VALVE_LABEL = { j: '1', k: '2', l: '3', ';': '4', q: '3', w: '2', e: '1', c: 'T' };

/**
 * The fingering to print under a missed note, as a stack of numbers:
 * ["1", "3"] for valves 1 and 3, ["0"] for open, ["4"] for trombone 4th position.
 */
export const fingeringLabels = (instrument, note) => {
  const keys = getCorrectKeys(instrument, note);
  if (instrument === 'trombone') {
    return [keys.find((key) => /^[1-7]$/.test(key)) || '1'];
  }
  const labels = keys
    .filter((key) => VALVE_LABEL[key])
    .map((key) => VALVE_LABEL[key])
    .sort();
  return labels.length > 0 ? labels : ['0'];
};

// Fastest tempo at which each scale has been played perfectly, kept in this
// browser. (A run now lasts exactly as long as the tempo says, so speed is
// measured in BPM rather than seconds.)
const BEST_KEY = 'brassflows_scales_best_bpm_v1';

export const bestKey = (instrument, scaleId) => `${instrument}:${scaleId}`;

export const loadBestBpms = () => {
  try {
    return JSON.parse(localStorage.getItem(BEST_KEY)) || {};
  } catch {
    return {};
  }
};

export const saveBestBpms = (bpms) => {
  try {
    localStorage.setItem(BEST_KEY, JSON.stringify(bpms));
  } catch {
    // storage unavailable; the best tempo just won't persist
  }
};